/*
 * Impact page — marketing page showcasing outcomes for children, parents, families.
 * Aesthetic matches About.jsx / HowItWorks.jsx (teal/cream/orange/navy).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import {
  Users,
  TrendingUp,
  Heart,
  Clock,
  BookOpen,
  Sparkles,
  Mic,
  Quote,
  ArrowRight,
  Palette,
  Handshake,
  Brain,
} from 'lucide-react';

// Palette
const TEAL = '#0d6d64';
const TEAL_DARK = '#0a544d';
const CREAM = '#F7F1E4';
const ORANGE = '#e67e22';
const NAVY = '#1a2340';

const STATS = [
  { icon: Users, target: 500, suffix: '+', label: 'Happy Children' },
  { icon: TrendingUp, target: 92, suffix: '%', label: 'Positive Growth Rate' },
  { icon: Heart, target: 98, suffix: '%', label: 'Parent Satisfaction' },
  { icon: Clock, target: 24, suffix: '/7', label: 'Monitoring Support' },
];

const STORIES = [
  {
    tag: 'Academic Growth',
    title: 'From Struggling to Excelling',
    text:
      'Ananya was shy and hesitant in her studies. With personalized attention and consistent listening sessions, she is now confident, engaged, and scoring higher than ever.',
    color: TEAL,
    image:
      'https://dacharya.com/wp-content/uploads/2026/05/academic_growth_left_image.png',
  },
  {
    tag: 'Engagement',
    title: 'From Passive to Actively Involved',
    text:
      'Rohan rarely participated in class. Through warm conversations and gentle encouragement, he is now an active learner who loves to raise his hand.',
    color: ORANGE,
    image:
      'https://dacharya.com/wp-content/uploads/2026/05/engagement_left_image.png',
  },
  {
    tag: 'Confidence Boost',
    title: 'From Hesitant to Confident Speaker',
    text:
      'Meera was hesitant to express herself. Our supportive observers helped her build confidence — she now speaks up with joy and clarity.',
    color: NAVY,
    image: 'https://dacharya.com/wp-content/uploads/2026/05/CONFIDENT-.jpg',
  },
];

const TESTIMONIALS = [
  {
    quote:
      "My daughter used to keep her feelings bottled up. After 2 months with Sanjaya, she's talking about her school day with such clarity.",
    name: 'Ananya',
    role: 'Parent of 7yo',
  },
  {
    quote:
      "The reports are so insightful. I finally understand the specific anxieties my son was facing in class. It's been a game changer.",
    name: 'Rohan K.',
    role: 'Parent of 9yo',
  },
  {
    quote:
      "The 5-minute call is the highlight of my child's day. He feels like a VIP. His confidence has grown tremendously.",
    name: 'Sunita L.',
    role: 'Parent of 11yo',
  },
];

const APPROACH = [
  {
    icon: BookOpen,
    title: 'Personalized Learning',
    text: "Customized learning paths based on each child's strengths and pace.",
  },
  {
    icon: Palette,
    title: 'Activity-Based Learning',
    text: 'Hands-on activities that make learning fun, interactive, and meaningful.',
  },
  {
    icon: Brain,
    title: 'Emotional Development',
    text: 'Building confidence, resilience, and a positive mindset in children.',
  },
  {
    icon: Handshake,
    title: 'Parent Collaboration',
    text: 'We work hand-in-hand with parents to support consistent growth.',
  },
];

// Simple count-up hook that animates when the element scrolls into view.
const useCountUp = (target, duration = 1600) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const step = (now) => {
              const progress = Math.min((now - start) / duration, 1);
              setValue(Math.round(target * progress));
              if (progress < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return [ref, value];
};

const StatCard = ({ icon: Icon, target, suffix, label, testid }) => {
  const [ref, value] = useCountUp(target);
  return (
    <div
      ref={ref}
      className="text-center px-4 py-6"
      data-testid={testid}
    >
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
        style={{ background: 'rgba(255, 255, 255, 0.15)' }}
      >
        <Icon className="w-7 h-7 text-white" />
      </div>
      <div className="text-4xl sm:text-5xl font-bold text-white mb-1">
        {value}
        {suffix}
      </div>
      <div className="text-sm uppercase tracking-widest text-white/70">
        {label}
      </div>
    </div>
  );
};

const Impact = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Georgia, serif' }}>
      <Navigation />

      {/* ═══════════ HERO ═══════════ */}
      <section
        className="pt-28 pb-16 px-4"
        style={{ background: CREAM }}
        data-testid="impact-hero-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div
              className="inline-block text-xs font-bold tracking-[0.3em] mb-4 px-3 py-1.5 rounded-full"
              style={{ color: TEAL_DARK, background: 'rgba(13,109,100,0.1)' }}
              data-testid="impact-hero-eyebrow"
            >
              OUR IMPACT
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: NAVY }}
              data-testid="impact-hero-title"
            >
              Shaping Future Together
            </h1>
            <p
              className="text-lg mb-8 leading-relaxed"
              style={{ color: '#4a5568' }}
              data-testid="impact-hero-subtitle"
            >
              The Observer helps children grow with confidence through listening,
              understanding, and guided emotional support.
            </p>

            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                className="rounded-full px-7 py-6 text-base text-white shadow-md"
                style={{ background: ORANGE }}
                data-testid="impact-hero-cta-primary"
              >
                Start Journey
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/plans')}
                className="rounded-full px-7 py-6 text-base border-2"
                style={{ borderColor: TEAL, color: TEAL }}
                data-testid="impact-hero-cta-secondary"
              >
                View Our Plans
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-center" data-testid="impact-hero-image">
            <div
              className="rounded-[2rem] overflow-hidden shadow-xl w-full max-w-lg aspect-[3/2]"
              style={{ background: 'linear-gradient(135deg, #c9a883 0%, #7d5f3f 100%)' }}
            >
              <img
                src="https://dacharya.com/wp-content/uploads/2026/05/607042b5-3d5e-4350-a13b-ab0b32a0f33d-1024x683.png"
                alt="Our Impact"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ STATS BAR ═══════════ */}
      <section
        className="py-14 px-4"
        style={{ background: NAVY }}
        data-testid="impact-stats-section"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2">
          {STATS.map((s, i) => (
            <StatCard
              key={s.label}
              icon={s.icon}
              target={s.target}
              suffix={s.suffix}
              label={s.label}
              testid={`impact-stat-${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ═══════════ SUCCESS STORIES ═══════════ */}
      <section className="py-20 px-4 bg-white" data-testid="impact-stories-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div
              className="inline-block text-xs font-bold tracking-[0.3em] mb-3 px-3 py-1.5 rounded-full"
              style={{ color: ORANGE, background: 'rgba(230,126,34,0.1)' }}
            >
              SUCCESS STORIES
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: NAVY }}
              data-testid="impact-stories-title"
            >
              Real Stories. Real Impact.
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>
              See how we are making a difference in the lives of children and families.
            </p>
          </div>

          <div className="space-y-8">
            {STORIES.map((story, i) => (
              <div
                key={story.title}
                className="grid md:grid-cols-5 gap-8 items-center rounded-3xl p-6 md:p-10 border shadow-sm"
                style={{ borderColor: 'rgba(13, 109, 100, 0.15)', background: '#fafafa' }}
                data-testid={`impact-story-${i + 1}`}
              >
                <div
                  className={`md:col-span-2 order-1 ${i % 2 === 1 ? 'md:order-2' : ''}`}
                >
                  <div
                    className="rounded-2xl aspect-[4/3] overflow-hidden flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${story.color}22 0%, ${story.color}44 100%)`,
                    }}
                  >
                    <img
                      src={story.image}
                      alt={story.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div
                      className="w-full h-full items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <Sparkles className="w-16 h-16" style={{ color: story.color }} />
                    </div>
                  </div>
                </div>
                <div
                  className={`md:col-span-3 order-2 ${i % 2 === 1 ? 'md:order-1' : ''}`}
                >
                  <div
                    className="inline-block text-xs font-semibold tracking-widest uppercase mb-3 px-3 py-1 rounded-full"
                    style={{
                      color: story.color,
                      background: `${story.color}15`,
                    }}
                  >
                    {story.tag}
                  </div>
                  <h3
                    className="text-2xl sm:text-3xl font-bold mb-4"
                    style={{ color: NAVY }}
                  >
                    {story.title}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#4a5568' }}>
                    {story.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ TESTIMONIALS ═══════════ */}
      <section
        className="py-20 px-4"
        style={{ background: CREAM }}
        data-testid="impact-testimonials-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div
              className="inline-block text-xs font-bold tracking-[0.3em] mb-3 px-3 py-1.5 rounded-full"
              style={{ color: TEAL_DARK, background: 'rgba(13,109,100,0.1)' }}
            >
              TRUSTED BY PARENTS
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold"
              style={{ color: NAVY }}
              data-testid="impact-testimonials-title"
            >
              Loved by Parents, Trusted by Families
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl p-7 shadow-md relative"
                data-testid={`impact-testimonial-${i + 1}`}
              >
                <Quote
                  className="absolute top-5 right-5 w-10 h-10 opacity-15"
                  style={{ color: TEAL }}
                />
                <p
                  className="text-base leading-relaxed mb-6"
                  style={{ color: '#4a5568' }}
                >
                  {t.quote}
                </p>
                <div>
                  <div className="font-bold text-lg" style={{ color: NAVY }}>
                    {t.name}
                  </div>
                  <div className="text-sm" style={{ color: TEAL }}>
                    {t.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ LEARNING APPROACH ═══════════ */}
      <section className="py-20 px-4 bg-white" data-testid="impact-approach-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: NAVY }}
              data-testid="impact-approach-title"
            >
              Our Learning Approach
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>
              We focus on holistic development by combining academics, creativity,
              and emotional growth.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 items-center">
            <div
              className="lg:col-span-2 flex justify-center"
              data-testid="impact-approach-image"
            >
              <div
                className="rounded-[2rem] overflow-hidden shadow-xl w-full max-w-md aspect-square"
                style={{
                  background: 'linear-gradient(135deg, #F7F1E4 0%, #e8d9b8 100%)',
                }}
              >
                <img
                  src="https://dacharya.com/wp-content/uploads/2026/05/school-girl.png"
                  alt="Learning approach"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>

            <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4">
              {APPROACH.map((a, i) => {
                const Icon = a.icon;
                return (
                  <div
                    key={a.title}
                    className="rounded-3xl p-6 border shadow-sm hover:shadow-lg transition-shadow bg-white"
                    style={{ borderColor: 'rgba(13, 109, 100, 0.15)' }}
                    data-testid={`impact-approach-${i + 1}`}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                      style={{ background: 'rgba(230, 126, 34, 0.12)' }}
                    >
                      <Icon className="w-6 h-6" style={{ color: ORANGE }} />
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: NAVY }}>
                      {a.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#4a5568' }}>
                      {a.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ CTA ═══════════ */}
      <section
        className="py-20 px-4"
        style={{ background: NAVY }}
        data-testid="impact-cta-section"
      >
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div
            className="flex justify-center md:justify-start order-2 md:order-1"
            data-testid="impact-cta-image"
          >
            <div
              className="rounded-[2rem] overflow-hidden shadow-2xl w-full max-w-sm aspect-[4/5]"
              style={{
                background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
              }}
            >
              <img
                src="https://dacharya.com/wp-content/uploads/2026/05/indian-girl-.png"
                alt="Join our community"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="text-center md:text-left order-1 md:order-2">
            <Mic
              className="w-14 h-14 mb-6 mx-auto md:mx-0"
              style={{ color: ORANGE }}
            />
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              data-testid="impact-cta-title"
            >
              Be a Part of the Change
            </h2>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Together, let&apos;s build a brighter future for our children. Join our
              community today.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/plans')}
              className="rounded-full px-8 py-6 text-base text-white shadow-md"
              style={{ background: ORANGE }}
              data-testid="impact-cta-button"
            >
              Enroll Your Child
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Impact;
